import React, { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ColumnProps } from 'antd/lib/table'
import { Formik } from 'formik'
import * as Yup from 'yup'

import {
  BulkJobResponse,
  JobLogFilter,
  JobLogItem,
  JobLogStatus,
  JobType,
  getAllEnabledJobs,
  getJobLogs,
  triggerJob,
} from 'core'

import useQueryRequest from 'common/hooks/useQueryRequest'

import { formatDateTime } from 'common/dateFormatters'

import { Button, Card, Col, DefaultSelectV2, Modal, Row, Tag, TextArea } from '../../../common/components'
import TableV2 from '../../../common/components/dataDisplay/TableV2'
import notification from '../../../common/components/feedback/Notification'
import { useCommandRequest } from '../../../common/hooks'
import isJSON from '../../../common/jsonUtilities'

const defaultFilter: JobLogFilter = {
  pageNumber: 1,
  pageSize: 25,
  orderColumn: 'startedOn',
  orderDescending: true,
  jobId: null,
  status: null,
  type: null,
  ended: null,
}

type JobLogsProps = {}

const JobLogs: React.FC<JobLogsProps> = (props) => {
  const { t: translate } = useTranslation()

  const [jobLogsFilter, setJobLogsFilter] = useState<JobLogFilter>(defaultFilter)
  const [jobLogs, isGetJobLogsQueryLoading, sendGetJobLogsQuery] = useQueryRequest(null)
  const [jobs] = useQueryRequest([], () => getAllEnabledJobs())

  const [modalResultContent, setModalResultContent] = useState('')
  const [sendCommand, isCommandLoading] = useCommandRequest()

  useEffect(() => {
    ;(async () => {
      await sendGetJobLogsQuery(() => getJobLogs(jobLogsFilter))
    })()
  }, [jobLogsFilter])

  const onJobLogsTableChange = (pagination: any, filters: any, sorter: any) => {
    setJobLogsFilter({
      ...jobLogsFilter,
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
      orderColumn: sorter.field || 'startedOn',
      orderDescending: sorter.order !== 'ascend',
    })
  }

  const onTriggerJob = async (jobId: string, data?: string) => {
    const result = await sendCommand(() =>
      triggerJob({
        jobId: jobId,
        data: data ?? '{}',
      }),
    )

    if (result.completed) {
      const timeout = setTimeout(() => {
        showSuccessNotification()

        setJobLogsFilter({
          ...defaultFilter,
          jobId: jobId,
        })

        clearTimeout(timeout)
      }, 3000)
    }
  }

  const columns: ColumnProps<JobLogItem>[] = [
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (item: any, record: JobLogItem) => {
        return (
          <>
            {record.description && (
              <div className={'mb-4'}>
                <b>Description: </b>
                {record.description}
              </div>
            )}

            <div>
              <b>Code: </b>
              {record.jobName}
            </div>
          </>
        )
      },
    },
    {
      title: 'Input',
      dataIndex: 'jobInput',
      key: 'jobInput',
      render: (item: string | null, record: JobLogItem) => {
        return item ? (
          <a href={'#'} onClick={() => setModalResultContent(item)}>
            View Input
          </a>
        ) : (
          <div>No Input</div>
        )
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: (item: any, record: JobLogItem) => {
        return <StatusTag status={record.status.id} />
      },
    },
    {
      title: translate('csi-dashboard.scheduled-jobs.triggered-by-label'),
      dataIndex: 'triggeredBy',
      key: 'triggeredBy',
    },
    {
      title: translate('csi-dashboard.scheduled-jobs.started-on-label'),
      dataIndex: 'startedOn',
      key: 'startedOn',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (item: string) => {
        return `${formatDateTime(item)} GMT`
      },
    },
    {
      title: translate('csi-dashboard.scheduled-jobs.ended-on-label'),
      dataIndex: 'endedOn',
      key: 'endedOn',
      render: (item: string | null) => {
        return item !== null ? `${formatDateTime(item)} GMT` : ''
      },
    },
    {
      title: 'Result',
      dataIndex: 'result',
      key: 'result',
      render: (item: string | null, record: JobLogItem) => {
        const bulkResponse = item && record.type.id == JobType.BulkJob ? (JSON.parse(item) as BulkJobResponse) : null

        return item ? (
          <>
            {bulkResponse && (
              <div className={'mb-8'}>
                <div className={'mb-4'}>Total Requested: {bulkResponse.totalRequested}</div>
                <div className={'mb-4'}>Total Succeeded: {bulkResponse.totalSucceeded}</div>
                <div className={'mb-4'}>Total Failed: {bulkResponse.failed.length}</div>
              </div>
            )}
            <a href={'#'} onClick={() => setModalResultContent(item)}>
              View Result
            </a>
          </>
        ) : (
          <div>No result</div>
        )
      },
    },
  ]

  const columnToSort: any = columns.find((c) => c.key === jobLogsFilter.orderColumn)
  if (columnToSort) {
    columnToSort.defaultSortOrder = jobLogsFilter.orderDescending ? 'descend' : 'ascend'
  }

  return (
    <>
      <Formik
        enableReinitialize={true}
        validationSchema={Yup.object({})}
        initialValues={{
          jobId: null,
          data: null,
          jobIdToTrigger: undefined,
          ended: undefined,
          status: undefined,
          type: undefined,
        }}
        onSubmit={() => {}}
      >
        {(formik) => (
          <form onSubmit={formik.handleSubmit}>
            <Card title={'Run Job'} className={'mb-16'}>
              <div className={'mb-16'}>
                <div className={'mb-8'}>
                  <DefaultSelectV2
                    name="jobIdToTrigger"
                    placeholder={'Select job to execute'}
                    label={null}
                    showSearch
                    items={jobs.map((x) => {
                      return {
                        name: x.name,
                        id: x.id,
                      }
                    })}
                  ></DefaultSelectV2>
                </div>

                <div className={'mb-8'}>
                  <TextArea name={'data'} placeholder={'Input data {}'} rows={10} />
                </div>

                <Button
                  type={'primary'}
                  disabled={!formik.values.jobIdToTrigger || isCommandLoading}
                  loading={isCommandLoading}
                  onClick={async () => {
                    formik.setFieldValue('jobId', formik.values.jobIdToTrigger)
                    await onTriggerJob(formik.values.jobIdToTrigger, formik.values.data)
                  }}
                  className="mr-16 w-100"
                  icon={'far fa-power-off'}
                >
                  Trigger Job
                </Button>
              </div>
            </Card>

            <Card title={'Job logs'}>
              <div className={'mb-16'}>
                <Row gutter={16}>
                  <Col span={9}>
                    <DefaultSelectV2
                      name="jobId"
                      placeholder={'Select job'}
                      label={null}
                      showSearch
                      items={jobs.map((x) => {
                        return {
                          name: x.name,
                          id: x.id,
                        }
                      })}
                      onChange={(value) => {
                        setJobLogsFilter({
                          ...defaultFilter,
                          jobId: value,
                        })
                      }}
                    ></DefaultSelectV2>
                  </Col>

                  <Col span={3}>
                    <DefaultSelectV2
                      name="status"
                      placeholder={'Select status'}
                      label={null}
                      items={[
                        { id: null, name: 'All' },
                        { id: JobLogStatus.Started, name: 'Started' },
                        { id: JobLogStatus.Succeeded, name: 'Succeeded' },
                        { id: JobLogStatus.PartiallySucceeded, name: 'Partially Succeeded' },
                        { id: JobLogStatus.Failed, name: 'Failed' },
                      ]}
                    ></DefaultSelectV2>
                  </Col>

                  <Col span={3}>
                    <DefaultSelectV2
                      name="type"
                      placeholder={'Select type'}
                      label={null}
                      items={[
                        { id: null, name: 'All' },
                        { id: JobType.Job, name: 'Job' },
                        { id: JobType.BulkJob, name: 'Bulk Job' },
                      ]}
                    ></DefaultSelectV2>
                  </Col>

                  <Col span={3}>
                    <DefaultSelectV2
                      name="ended"
                      placeholder={'Ended or Not Ended'}
                      label={null}
                      items={[
                        { id: null, name: 'All' },
                        { id: true, name: 'Ended' },
                        { id: false, name: 'Not Ended' },
                      ]}
                    ></DefaultSelectV2>
                  </Col>

                  <Col>
                    <Button
                      type={'primary'}
                      disabled={isGetJobLogsQueryLoading}
                      loading={isGetJobLogsQueryLoading}
                      onClick={() => {
                        setJobLogsFilter({
                          ...jobLogsFilter,
                          jobId: formik.values.jobId,
                          status: formik.values.status,
                          type: formik.values.type,
                          ended: formik.values.ended,
                        })
                      }}
                      className="mr-16 w-100"
                      icon={'fal fa-search'}
                    >
                      Search
                    </Button>
                  </Col>

                  <Col>
                    <Button
                      disabled={isGetJobLogsQueryLoading}
                      loading={isGetJobLogsQueryLoading}
                      onClick={() => {
                        formik.setValues({
                          jobId: undefined,
                          status: undefined,
                          type: undefined,
                          ended: undefined,
                          data: formik.values.data,
                          jobIdToTrigger: formik.values.jobIdToTrigger,
                        })

                        setJobLogsFilter({
                          ...defaultFilter,
                        })
                      }}
                      className="mr-16 w-100"
                      icon={'fal fa-redo-alt'}
                    >
                      Reset
                    </Button>
                  </Col>
                </Row>
              </div>

              <TableV2
                dataSource={jobLogs?.items}
                onChange={onJobLogsTableChange}
                loading={isGetJobLogsQueryLoading}
                columns={columns}
                pagination={{
                  total: jobLogs?.totalItemCount,
                  pageSize: jobLogs?.pageSize,
                  current: jobLogs?.pageNumber,
                }}
                rowKey={(record) => `${record.jobName}-${record.startedOn}`}
              />
            </Card>
          </form>
        )}
      </Formik>

      <Modal
        width={'960px'}
        visible={!!modalResultContent}
        onCancel={() => setModalResultContent('')}
        onOk={() => setModalResultContent('')}
      >
        {modalResultContent && (
          <div>
            {isJSON(modalResultContent) && <pre>{JSON.stringify(JSON.parse(modalResultContent), undefined, 2)}</pre>}
            {!isJSON(modalResultContent) && <>{modalResultContent}</>}
          </div>
        )}
      </Modal>
    </>
  )
}

const showSuccessNotification = () => {
  notification.open({
    message: 'Job scheduled successfully',
    type: 'success',
  })
}

const StatusTag: React.FC<{ status: JobLogStatus }> = ({ status }) => {
  switch (status) {
    case JobLogStatus.Failed:
      return <Tag color={'red'}>Failed</Tag>
    case JobLogStatus.Succeeded:
      return <Tag color={'green'}>Success</Tag>
    case JobLogStatus.PartiallySucceeded:
      return <Tag color={'orange'}>Partially Succeeded</Tag>
    case JobLogStatus.Started:
      return <Tag color={'blue'}>Started</Tag>
    default:
      return <Tag color={'blue'}>Started</Tag>
  }
}

const TypeTag: React.FC<{ type: JobType }> = ({ type }) => {
  switch (type) {
    case JobType.Job:
      return <Tag color={'purple'}>Job</Tag>
    case JobType.BulkJob:
      return <Tag color={'cyan'}>Bulk Job</Tag>
    default:
      return <Tag color={'purple'}>Job</Tag>
  }
}

export default JobLogs
